import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { graphql, useQuery } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';

import SpecialtyEventUploadLayout from '../SpecialtyEventUploadView/components/SpecialtyEventUploadLayout';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import NullState from '../../../apps/pro/components/GiftingEventAddonItemsPurchase/components/NullState';
import SuccessStep from '../../../apps/pro/components/GiftingEventAddonItemsPurchase/components/SuccessStep';
import PurchaseStep from '../../../apps/pro/components/GiftingEventAddonItemsPurchase/components/PurchaseStep';
import useGiftingEventAddonPurchase from '../../../apps/pro/components/GiftingEventAddonItemsPurchase/hooks/useGiftingEventAddonPurchase';
import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import { STRIPE_CHECKOUT_MODAL } from '../../../components/global/StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import useOpenModal from '../../../hooks/useOpenModal';
import { MeQuery } from '../../../graphql/queries/account';
import { AlbumFolderForGiftingEventAddonItems } from '../../../graphql/queries/album-folder';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
};

const STEPS = {
  NULL: 'NULL',
  PURCHASE: 'PURCHASE',
  SUCCESS: 'SUCCESS',
  LOADING: 'LOADING',
};

const SpecialtyEventGiftView = ({ albumFolder, location }) => {
  const [step, setStep] = useState(STEPS.LOADING);
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const { data: { me: user } = {} } = useQuery(MeQuery);
  const { eventAddonItemIds } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });

  const {
    loadingEventAddonItems,
    eventAddonItems,
    onCheckout,
    submitting,
    selectedEventAddonItemIds,
    onEventAddonItemToggle,
    success,
    possessiveCelebratedName,
  } = useGiftingEventAddonPurchase({
    albumFolderId: albumFolder?.id,
    defaultSelectedEventAddonItemIds: eventAddonItemIds,
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
    onCheckoutError: ({ error }) =>
      openAlert({
        message: 'There was an error purchasing the selected items',
        info: getErrorString(error),
      }),
  });

  useEffect(() => {
    if (success) {
      return;
    }

    if (albumFolder && eventAddonItems?.length > 0 && !loadingEventAddonItems) {
      setStep(STEPS.PURCHASE);
    } else if (albumFolder && !loadingEventAddonItems) {
      setStep(STEPS.NULL);
    }
  }, [albumFolder, eventAddonItems, loadingEventAddonItems]);

  useEffect(() => {
    if (success) {
      setStep(STEPS.SUCCESS);
    }
  }, [success]);

  const onPurchase = () => {
    if (!albumFolder) {
      // We cannot perform a checkout without albumFolderId
      return;
    }

    openModal(STRIPE_CHECKOUT_MODAL, {
      buttonLabel: 'Complete payment',
      // If the event is FREE, will not require CC
      cardRequired: false,
      onSuccess: token =>
        onCheckout({
          token,
          albumFolderId: albumFolder.id,
          eventAddonItemIds: selectedEventAddonItemIds,
        }),
      accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_addon_items: selectedEventAddonItemIds.map(id => ({
            event_addon_item_id: id,
            quantity: 1,
            album_folder_id: albumFolder.id,
            for_gift: true,
          })),
          promo_code: promoCode,
        };
      },
      defaultName: user?.name ?? '',
      defaultEmail: user?.email_address ?? '',
    });
  };

  const renderStep = () => {
    switch (step) {
      case STEPS.NULL:
        return (
          <NullState possessiveCelebratedName={possessiveCelebratedName} />
        );
      case STEPS.PURCHASE:
        return (
          <PurchaseStep
            selectedEventAddonItemIds={selectedEventAddonItemIds}
            onEventAddonItemToggle={onEventAddonItemToggle}
            eventAddonItems={eventAddonItems}
            onPurchase={onPurchase}
            submitting={submitting}
            possessiveCelebratedName={possessiveCelebratedName}
          />
        );
      case STEPS.SUCCESS:
        return <SuccessStep />;
      case STEPS.LOADING:
        return <FullScreenLoader />;
      default:
        return null;
    }
  };

  return (
    <SpecialtyEventUploadLayout contentClassName="is-gifting">
      <div className="specialty-event-gift-view">{renderStep()}</div>
    </SpecialtyEventUploadLayout>
  );
};

SpecialtyEventGiftView.propTypes = propTypes;

const withAlbumFolderForGifting = graphql(
  AlbumFolderForGiftingEventAddonItems,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      loading,
      albumFolder,
    }),
  },
);

export default compose(
  withRouter,
  withAlbumFolderForGifting,
)(SpecialtyEventGiftView);
